// Curated map from each essay to the Shrinktionary term pages that define its
// core words. Every path below is sitemap-exact against
// shrinktionary.com/sitemap-index.xml and against the canonical id list in
// shrinktionary.com/concepts.json at the time of curation. The audit script
// at scripts/audit-network-urls.mjs re-checks these against the live
// Shrinktionary sitemap on every build.
//
// Editorial framing:
//   Shrinktionary is the network's language layer. The essays on this site
//   use the same words. This block gives readers the canonical definition
//   next to each essay so a reader who is not sure what "rumination" means
//   can look it up without leaving the network.
//
// UTM shape:
//   utm_source=shariqrefai
//   utm_medium=network_journey
//   utm_campaign=auto_perspectives_{essay_slug}_words

export interface ShrinktionaryEntry {
  /** Human label rendered as link text. Usually just the term itself. */
  label: string;
  /** Canonical term slug from shrinktionary.com/concepts.json. */
  slug: string;
}

/**
 * Per-essay Shrinktionary word list. Order matters: primary term first, then
 * supporting terms. Cap at four words per essay to keep the block scannable.
 */
export const ESSAY_SHRINKTIONARY_WORDS: Record<string, ShrinktionaryEntry[]> = {
  "why-your-mind-feels-loudest-when-trying-to-rest": [
    { label: "rumination", slug: "rumination" },
    { label: "overthinking", slug: "overthinking" },
    { label: "insomnia", slug: "insomnia" },
  ],
  "difference-between-a-thought-and-thinking": [
    { label: "overthinking", slug: "overthinking" },
    { label: "rumination", slug: "rumination" },
    { label: "cognitive distortion", slug: "cognitive-distortion" },
  ],
  "why-thinking-harder-usually-makes-anxiety-worse": [
    { label: "overthinking", slug: "overthinking" },
    { label: "rumination", slug: "rumination" },
    { label: "anxiety", slug: "anxiety" },
    { label: "cognitive distortion", slug: "cognitive-distortion" },
  ],
  "why-your-brain-replays-conversations-long-after-they-end": [
    { label: "rumination", slug: "rumination" },
    { label: "overthinking", slug: "overthinking" },
  ],
  "why-high-performers-can-still-feel-anxious": [
    { label: "high-functioning anxiety", slug: "high-functioning-anxiety" },
    { label: "burnout", slug: "burnout" },
    { label: "perfectionism", slug: "perfectionism" },
  ],
  "why-your-nervous-system-thinks-everything-is-urgent": [
    { label: "hypervigilance", slug: "hypervigilance" },
    { label: "sympathetic nervous system", slug: "sympathetic-nervous-system" },
    { label: "vagus nerve", slug: "vagus-nerve" },
  ],
  "why-popular-mental-health-commentary-falls-short-in-the-moment": [
    { label: "panic attack", slug: "panic-attack" },
    { label: "anxiety", slug: "anxiety" },
    { label: "cognitive behavioral therapy", slug: "cbt" },
  ],
  "what-modern-psychiatry-gets-right": [
    { label: "SSRI", slug: "ssri" },
    { label: "antidepressant", slug: "antidepressant" },
    { label: "cognitive behavioral therapy", slug: "cbt" },
  ],
  // digital-mental-health-needs-more-than-convenience: telepsychiatry and
  // psychiatry are not (yet) canonical Shrinktionary terms. Left empty.
};

const SHRINKTIONARY_URL = "https://shrinktionary.com";

/**
 * Build a Shrinktionary term URL with the standard reciprocity UTM tuple.
 *   campaignSlug is the essay slug (e.g. "perspectives_rumination_words").
 */
export function shrinktionaryUrl(slug: string, campaignSlug: string): string {
  const params = new URLSearchParams({
    utm_source: "shariqrefai",
    utm_medium: "network_journey",
    utm_campaign: campaignSlug,
  });
  return `${SHRINKTIONARY_URL}/terms/${slug}/?${params.toString()}`;
}
